import { computed, ref } from "vue";
import { defineStore } from "pinia";
import partnerCompanyNamesService from "@shared/services/salesforce/partnerCompanyNames/partnerCompanyNames.ts";
import alliancePartnerManagerService from "@shared/services/salesforce/alliancePartnerManager/alliancePartnerManager.ts";
import logger from "@shared/utils/logger.ts";
import { PartnerCompanyNamesResponse } from "@shared/services/salesforce/partnerCompanyNames/response.ts";
import { AlliancePartnerManagerResponse } from "@shared/services/salesforce/alliancePartnerManager/response.ts";
import { useNotifications } from "@shared/module-transformers/useNotifications.ts";
import { messagesService } from "@shared/services/messages.ts";
import { analyticsService } from "@shared/services/analytics.ts";

export const usePartnerStore = defineStore("partner", () => {
  // State
  const _isRightPaneOpen = ref<boolean>(false);
  const _isPartnerCompanyNamesLoading = ref<boolean>(false);
  const _currentPartnerId = ref<string>("");
  const _partnerCompanyNames = ref<PartnerCompanyNamesResponse | null>(null);
  const _alliancePartnerManager = ref<AlliancePartnerManagerResponse | null>(
    null,
  );
  const _isAlliancePartnerManagerLoading = ref<boolean>(false);

  // Getters
  const isRightPaneOpen = computed(() => _isRightPaneOpen.value);
  const currentPartnerId = computed(() => _currentPartnerId.value);
  const partnerCompanyNames = computed(() => _partnerCompanyNames.value);
  const selectedPartnershipName = computed(
    () =>
      _partnerCompanyNames.value?.partners.find(
        (p) => p.crmPartnerId === _currentPartnerId.value,
      )?.partnerCompanyName || "",
  );
  const moreThanOnePartnership = computed(
    () =>
      _partnerCompanyNames.value?.partners?.length &&
      _partnerCompanyNames.value.partners.length > 1,
  );
  const isPartnerCompanyNamesLoading = computed(
    () => _isPartnerCompanyNamesLoading.value,
  );
  const alliancePartnerManager = computed(() => _alliancePartnerManager.value);
  const hasPartnerManager = computed(() => {
    if (_alliancePartnerManager.value) {
      const manager = _alliancePartnerManager.value?.alliancePartnerManager;
      if (
        manager.lastName !== "" &&
        manager.firstName !== "" &&
        manager.email !== ""
      ) {
        return true;
      }
    }
    return false;
  });
  const isAlliancePartnerManagerLoading = computed(
    () => _isAlliancePartnerManagerLoading.value,
  );

  // Actions
  const toggleRightPaneOpen = async () => {
    _isRightPaneOpen.value = !_isRightPaneOpen.value;
  };

  const setPartnerId = (id: string) => {
    analyticsService.setPartnerProperties();
    _currentPartnerId.value = id;
  };

  const clearPartnerId = () => {
    _currentPartnerId.value = "";
  };

  const retrievePartners = async () => {
    if (_partnerCompanyNames.value) {
      return;
    }

    _isPartnerCompanyNamesLoading.value = true;
    try {
      _partnerCompanyNames.value = await partnerCompanyNamesService();
    } catch (e) {
      logger.error(`Error: ${e}`);
      useNotifications().error(messagesService.app_something_went_wrong);
    }
    _isPartnerCompanyNamesLoading.value = false;
  };

  const retrieveAlliancePartnerManager = async () => {
    _isAlliancePartnerManagerLoading.value = true;
    try {
      _alliancePartnerManager.value = await alliancePartnerManagerService();
    } catch (e) {
      useNotifications().error(messagesService.app_something_went_wrong);
      logger.error(`Error: ${e}`);
    }
    _isAlliancePartnerManagerLoading.value = false;
  };

  return {
    isRightPaneOpen,
    currentPartnerId,
    partnerCompanyNames,
    selectedPartnershipName,
    moreThanOnePartnership,
    isPartnerCompanyNamesLoading,
    alliancePartnerManager,
    hasPartnerManager,
    isAlliancePartnerManagerLoading,
    toggleRightPaneOpen,
    setPartnerId,
    clearPartnerId,
    retrievePartners,
    retrieveAlliancePartnerManager,
  };
});
